import { ReactNode } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface StatDef {
  label: string;
  value: ReactNode;
  sub?: string;
  icon: ReactNode;
  color: string;
  trend?: string;
}

const StatCard = ({ label, value, sub, icon, color, trend }: StatDef) => (
  <Card
    sx={{
      minHeight: 128,
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 100,
        height: 100,
        right: -44,
        top: -48,
        borderRadius: '50%',
        bgcolor: alpha(color, 0.055),
      },
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.7, p: { xs: 2, md: 2.2 }, '&:last-child': { pb: { xs: 2, md: 2.2 } } }}>
      <Box
        sx={{
          width: 52,
          height: 52,
          flexShrink: 0,
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(color, 0.11),
          color,
          border: `1px solid ${alpha(color, 0.08)}`,
          '& svg': { fontSize: 25 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }} noWrap>{label}</Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 850,
            lineHeight: 1.22,
            mt: 0.1,
            fontVariantNumeric: 'tabular-nums',
            fontSize: { xs: 22, md: 25 },
          }}
        >
          {value}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, mt: 0.35 }}>
          {trend && <Typography variant="caption" sx={{ color, fontWeight: 800 }}>{trend}</Typography>}
          {sub && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{sub}</Typography>}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const StatRow = ({ stats }: { stats: StatDef[] }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        lg: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))`,
      },
      gap: { xs: 1.25, md: 1.75 },
      mb: { xs: 2, md: 2.5 },
    }}
  >
    {stats.map((s, i) => <StatCard key={`${s.label}-${i}`} {...s} />)}
  </Box>
);
